import React, { useState } from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AppNavItem, NavSectionId } from '../navigation/appNavConfig';
import { isNavItemActive, NAV_SECTION_LABELS } from '../navigation/appNavConfig';
import { colors, typography } from '../styles/theme';
import { useMe } from '../hooks/useMe';
import { logout, isAuthenticated } from '../auth/okta';

export const APP_DRAWER_WIDTH = 260;
export const APP_DRAWER_WIDTH_COLLAPSED = 72;

export interface AppSidebarProps {
  sections: { section: NavSectionId; items: AppNavItem[] }[];
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  showDesktopToggle?: boolean;
  showMobileClose?: boolean;
  onMobileClose?: () => void;
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  sections,
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
  showDesktopToggle = false,
  showMobileClose = false,
  onMobileClose,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { me } = useMe();
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);

  const displayEmail = me?.email ?? 'User';
  const roleLabel = (me?.role_names && me.role_names.length > 0) ? me.role_names.join(', ') : (me?.role_name || 'Member');

  const handleClick = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const handleLogout = () => {
    setProfileMenuAnchor(null);
    logout();
    window.location.href = '/';
  };

  const navList = (
    <List component="nav" aria-label="Application" dense sx={{ px: collapsed ? 0.5 : 0.5, py: 1.5, flex: 1, overflow: 'auto' }}>
      {sections.map(({ section, items: sectionItems }, sectionIndex) => (
        <React.Fragment key={section}>
          {!collapsed && (
            <ListSubheader
              disableSticky
              component="div"
              sx={{
                position: 'static',
                typography: 'overline',
                fontSize: '0.65rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                lineHeight: 2,
                color: colors.sidebar.subtleText,
                px: 2,
                pt: sectionIndex === 0 ? 0 : 2,
                pb: 0.75,
              }}
            >
              {NAV_SECTION_LABELS[section].toUpperCase()}
            </ListSubheader>
          )}
          {sectionItems.map((item) => {
            const Icon = item.icon;
            const selected = isNavItemActive(pathname, item);
            const button = (
              <ListItemButton
                selected={selected}
                onClick={() => handleClick(item.path)}
                sx={{
                  borderRadius: 2,
                  justifyContent: collapsed ? 'center' : undefined,
                  px: collapsed ? 1 : 2,
                  py: 1,
                  minHeight: 44,
                  mx: collapsed ? 0.5 : 1,
                  mb: 0.5,
                  color: 'rgba(255, 255, 255, 0.95)',
                  '&:hover': {
                    backgroundColor: colors.sidebar.hover,
                  },
                  '&.Mui-selected': {
                    backgroundColor: colors.sidebar.selected,
                    color: colors.text.light,
                    borderLeft: 'none',
                    '&:hover': {
                      backgroundColor: colors.sidebar.selected,
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 40,
                    justifyContent: 'center',
                    color: 'inherit',
                  }}
                >
                  <Icon fontSize="small" />
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: selected ? typography.fontWeight.semibold : typography.fontWeight.medium,
                      fontSize: typography.fontSize.md,
                      color: 'inherit',
                    }}
                  />
                )}
              </ListItemButton>
            );

            return (
              <ListItem key={item.id} disablePadding>
                {collapsed ? (
                  <Tooltip title={item.label} placement="right">
                    {button}
                  </Tooltip>
                ) : (
                  button
                )}
              </ListItem>
            );
          })}
        </React.Fragment>
      ))}
    </List>
  );

  const profileFooter =
    me && isAuthenticated() ? (
      <Box
        sx={{
          borderTop: `1px solid ${colors.sidebar.divider}`,
          px: collapsed ? 0.5 : 1.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        {collapsed ? (
          <Tooltip title={`${displayEmail} · ${roleLabel}`} placement="right">
            <IconButton
              size="small"
              aria-label="Account menu"
              aria-controls={profileMenuAnchor ? 'sidebar-account-menu' : undefined}
              aria-haspopup="true"
              onClick={(e) => setProfileMenuAnchor(e.currentTarget)}
              sx={{ color: 'rgba(255,255,255,0.85)', mx: 'auto' }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                noWrap
                sx={{
                  fontWeight: typography.fontWeight.semibold,
                  fontSize: typography.fontSize.sm,
                  color: colors.text.light,
                  lineHeight: 1.3,
                }}
                title={displayEmail}
              >
                {displayEmail}
              </Typography>
              <Typography
                noWrap
                sx={{
                  fontSize: typography.fontSize.xs,
                  color: colors.sidebar.subtleText,
                  lineHeight: 1.3,
                }}
              >
                {roleLabel}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label="Account menu"
              aria-controls={profileMenuAnchor ? 'sidebar-account-menu' : undefined}
              aria-haspopup="true"
              onClick={(e) => setProfileMenuAnchor(e.currentTarget)}
              sx={{ color: 'rgba(255,255,255,0.85)', flexShrink: 0 }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </>
        )}
        <Menu
          id="sidebar-account-menu"
          anchorEl={profileMenuAnchor}
          open={Boolean(profileMenuAnchor)}
          onClose={() => setProfileMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              setProfileMenuAnchor(null);
              handleLogout();
            }}
          >
            Log out
          </MenuItem>
        </Menu>
      </Box>
    ) : null;

  return (
    <Box
      sx={{
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.sidebar.background,
        color: colors.text.light,
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 64, md: 72 },
          px: collapsed && !showMobileClose ? 1 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexShrink: 0,
        }}
      >
        {showMobileClose ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: typography.fontFamily.primary,
                  fontWeight: typography.fontWeight.bold,
                  fontSize: '1.25rem',
                  color: colors.text.light,
                }}
              >
                OneSec
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onMobileClose}
              aria-label="Close navigation menu"
              sx={{ color: 'rgba(255,255,255,0.9)' }}
            >
              <CloseIcon />
            </IconButton>
          </>
        ) : collapsed ? (
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton
              size="small"
              onClick={onToggleCollapsed}
              aria-label="Expand sidebar"
              sx={{ color: colors.text.light, mx: 'auto' }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: typography.fontFamily.primary,
                  fontWeight: typography.fontWeight.bold,
                  fontSize: '1.25rem',
                  color: colors.text.light,
                }}
              >
                OneSec
              </Typography>
            </Box>
            {showDesktopToggle && (
              <Tooltip title="Collapse sidebar" placement="bottom">
                <IconButton
                  size="small"
                  onClick={onToggleCollapsed}
                  aria-label="Collapse sidebar"
                  sx={{ color: 'rgba(255,255,255,0.85)' }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Toolbar>
      <Divider sx={{ borderColor: colors.sidebar.divider }} />
      {navList}
      {profileFooter}
    </Box>
  );
};

export interface AppDrawerShellProps extends AppSidebarProps {
  open: boolean;
  onClose: () => void;
  desktopDrawerWidth: number;
  desktopCollapsed: boolean;
  onToggleDesktopCollapsed: () => void;
}

const drawerPaperSx = {
  boxSizing: 'border-box' as const,
  borderRight: 'none',
  backgroundColor: colors.sidebar.background,
  color: colors.text.light,
};

export function AppDrawerShell({
  open,
  onClose,
  sections,
  onNavigate,
  desktopDrawerWidth,
  desktopCollapsed,
  onToggleDesktopCollapsed,
}: AppDrawerShellProps) {
  const permanentDrawer = (
    <AppSidebar
      sections={sections}
      onNavigate={onNavigate}
      collapsed={desktopCollapsed}
      onToggleCollapsed={onToggleDesktopCollapsed}
      showDesktopToggle
    />
  );

  const mobileDrawer = (
    <AppSidebar
      sections={sections}
      onNavigate={onNavigate}
      showMobileClose
      onMobileClose={onClose}
    />
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { xs: 0, md: desktopDrawerWidth },
        flexShrink: { md: 0 },
        transition: (theme) =>
          theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
      }}
      aria-label="Side navigation"
    >
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: APP_DRAWER_WIDTH,
            ...drawerPaperSx,
          },
        }}
      >
        {mobileDrawer}
      </Drawer>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: desktopDrawerWidth,
            ...drawerPaperSx,
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            overflowX: 'hidden',
          },
        }}
      >
        {permanentDrawer}
      </Drawer>
    </Box>
  );
}

export default AppSidebar;
